import Fab from '@mui/material/Fab'
import Tooltip from '@mui/material/Tooltip'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import DisabledReason from '../system/DisabledReason'
import useAiAccess from '../../hooks/useAiAccess'
import useWhoToAsk from '../../hooks/useWhoToAsk'
import { refuse } from '../../utils/permissionReason'

interface DiscussStoryboardFabProps {
  /** The page has been read and the person is a signed-in Manager. */
  visible: boolean
  /** The customer NAME whose AI access decides (the artifact's customer). */
  customer: string
  /** The page helper is open: the button steps aside. */
  open: boolean
  onDiscuss: () => void
}

/**
 * "Discuss this storyboard" (Adam, 16 Sep 2026): the one control on a framed
 * storyboard. Pressing it opens the page helper (the agent, with the
 * storyboard's tools and its summary), so the person can talk the findings
 * through instead of reading them. Bottom right; it hides itself while the
 * helper is open. Below agent access it is shown disabled with the reason.
 */
export default function DiscussStoryboardFab({ visible, customer, open, onDiscuss }: DiscussStoryboardFabProps) {
  // The access hooks only run once the button is going to show, so an
  // anonymous viewer's page never reads the customer's AI access.
  if (!visible || open) return null
  return <DiscussFab customer={customer} onDiscuss={onDiscuss} />
}

function DiscussFab({ customer, onDiscuss }: { customer: string; onDiscuss: () => void }) {
  const aiAccess = useAiAccess(customer)
  const who = useWhoToAsk(customer)
  const reason = aiAccess.levelAllows('agent_sessions')
    ? undefined
    : refuse("You can't discuss this storyboard without agent access.", who).reason
  return (
    <Tooltip title="Talk the findings through with the agent" placement="left">
      <span style={{ position: 'fixed', right: 28, bottom: 28 }}>
        <DisabledReason reason={reason}>
          <Fab
            variant="extended"
            color="info"
            onClick={onDiscuss}
            aria-label="Discuss this storyboard"
            data-testid="discuss-storyboard"
            sx={{ zIndex: (t) => t.zIndex.speedDial, textTransform: 'none', fontWeight: 600, gap: 1, pr: 2.5 }}
          >
            <ForumOutlinedIcon />
            Discuss this storyboard
          </Fab>
        </DisabledReason>
      </span>
    </Tooltip>
  )
}
